import { KPI_ACTIVE_HEARTBEAT_MS } from '@klareospace/pharos-lib-types';
import type { Query } from '@tanstack/react-query';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Default polling interval (ms) used by `createPendingRefetchResolver` when no override is passed. */
export const PENDING_REFETCH_INTERVAL_MS = 15_000;

/** Re-exported so `@klareospace/pharos-lib-front-utils` keeps exposing this constant unchanged. */
export { KPI_ACTIVE_HEARTBEAT_MS };

// ─── Utils ────────────────────────────────────────────────────────────────────

/**
 * Builds a `refetchInterval` resolver for `useQuery`: polls at `intervalMs` while `isPending`
 * returns `true` for the query's current data, stops (returns `false`) once it returns `false`.
 *
 * Shared by any query that needs to poll until a server-side computation completes — e.g. a
 * still-`pending` import, a KPI still missing its `metrics`. Callers own the domain-specific
 * `isPending` predicate; this factory only owns the generic polling/stopping mechanics.
 */
export function createPendingRefetchResolver<T>(
  isPending: (data: T | undefined) => boolean,
  intervalMs: number = PENDING_REFETCH_INTERVAL_MS,
): (query: Query<T>) => number | false {
  return (query: Query<T>): number | false => (isPending(query.state.data) ? intervalMs : false);
}
